import { components as c } from "@/components";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateDiaryFlowSearchSchema } from "@/features/patients/update/updateDiaryFlowSearch";
import { Button } from "@patient-watch/ui/button";
import { Form } from "@patient-watch/ui/form";
import dayjs from "dayjs";
import { z } from "zod";
import { useDiaryByIdQuery } from "@/features/diaries/api/diaryQueries";
import {
    NEW_DATE_OPTION,
    SharedAttributeDateOptions,
} from "@/features/shared_attributes/components/SharedAttributeDateOptions";
import {
    buildSharedAttributesFromDiary,
    getSharedAttributeContactId,
} from "@/features/shared_attributes/components/SharedAttributeDisplay";

const dateFormSchema = z.object({
    date_group: z.string().min(1),
    reference_date: z.string(),
});

type DateFormValues = z.infer<typeof dateFormSchema>;

const AddDatePage = () => {
    const copy = usePatientsCopy();
    const { diaryId, eventId, trackerId } = Route.useParams();
    const search = Route.useSearch();
    const { type } = search;
    const navigate = useNavigate();
    const { data: diary } = useDiaryByIdQuery(diaryId);
    const dateGroups = diary ? buildSharedAttributesFromDiary(diary) : [];

    const form = useForm<DateFormValues>({
        resolver: zodResolver(dateFormSchema),
        defaultValues: {
            date_group: search.shared_attribute_id ?? (dateGroups[0]?.shared_attribute_id || NEW_DATE_OPTION),
            reference_date: search.reference_date ?? dayjs().toISOString(),
        },
    });

    const selectedGroup = form.watch("date_group");
    const didDefaultDateGroup = useRef(false);

    useEffect(() => {
        if (didDefaultDateGroup.current || search.shared_attribute_id) return;
        const firstId = dateGroups[0]?.shared_attribute_id;
        if (!firstId) return;
        didDefaultDateGroup.current = true;
        if (form.getValues("date_group") === NEW_DATE_OPTION) {
            form.setValue("date_group", firstId);
        }
    }, [dateGroups, form, search.shared_attribute_id]);

    const goNext = (nextSearch: typeof search & { reference_date: string; shared_attribute_id?: string }) => {
        if (type === "both" || type === "questionnaire") {
            void navigate({
                to: "/patients/$diaryId/update/$eventId/$trackerId/questionnaire",
                params: { diaryId, eventId, trackerId },
                search: nextSearch,
            });
            return;
        }

        const contactId = getSharedAttributeContactId(diary!, nextSearch.shared_attribute_id);
        if (nextSearch.shared_attribute_id && contactId) {
            void navigate({
                to: "/patients/$diaryId/update/$eventId/$trackerId/message",
                params: { diaryId, eventId, trackerId },
                search: { ...nextSearch, contact_id: contactId },
            });
            return;
        }

        void navigate({
            to: "/patients/$diaryId/update/$eventId/$trackerId/contact",
            params: { diaryId, eventId, trackerId },
            search: nextSearch,
        });
    };

    const handleSubmit = (data: DateFormValues) => {
        if (data.date_group !== NEW_DATE_OPTION) {
            const selected = dateGroups.find((sa) => sa.shared_attribute_id === data.date_group);
            goNext({
                ...search,
                type,
                shared_attribute_id: data.date_group,
                reference_date: selected?.reference_date || selected?.start_date || data.reference_date,
            });
            return;
        }

        const { shared_attribute_id: _existing, ...rest } = search;
        goNext({
            ...rest,
            type,
            reference_date: data.reference_date,
        });
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
                <c.cc.Title words={copy.referenceDateTitle} description={copy.pickExistingDateHelp} />
                <SharedAttributeDateOptions
                    options={dateGroups}
                    value={selectedGroup}
                    onChange={(value) => form.setValue("date_group", value, { shouldValidate: true })}
                    existingLabel={copy.existingDateGroup}
                    newDateLabel={copy.newDate}
                    newDateHelp={copy.newDateHelp}
                />
                {selectedGroup === NEW_DATE_OPTION ? <c.fc.DateTime name="reference_date" /> : null}
                <Button size="lg" className="w-full" type="submit" disabled={!diary}>
                    {copy.continueLabel}
                </Button>
            </form>
        </Form>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId/update/$eventId/$trackerId/date")({
    component: AddDatePage,
    validateSearch: updateDiaryFlowSearchSchema,
});
